import type { PoliticalMatchLocalState } from '@/models/political-match';

const databaseName = 'verdeconfirma-political-match';
const databaseVersion = 1;
const storeName = 'local-state';

const memoryStateStore = new Map<string, PoliticalMatchLocalState>();

type IndexedDbState = IDBDatabase | null;

let openDatabasePromise: Promise<IndexedDbState> | null = null;

function supportsIndexedDb(): boolean {
  return typeof window !== 'undefined' && window.indexedDB !== undefined && window.indexedDB !== null;
}

function cloneState(state: PoliticalMatchLocalState): PoliticalMatchLocalState {
  return JSON.parse(JSON.stringify(state)) as PoliticalMatchLocalState;
}

function openPoliticalMatchDatabase(): Promise<IndexedDbState> {
  if (!supportsIndexedDb()) {
    return Promise.resolve(null);
  }

  if (!openDatabasePromise) {
    openDatabasePromise = new Promise((resolve) => {
      const request = window.indexedDB.open(databaseName, databaseVersion);

      request.onupgradeneeded = () => {
        const db = request.result;

        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: 'questionnaireVersion' });
        }
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        resolve(null);
      };

      request.onblocked = () => {
        resolve(null);
      };
    });
  }

  return openDatabasePromise;
}

async function readStateFromIndexedDb(questionnaireVersion: string): Promise<PoliticalMatchLocalState | null> {
  const db = await openPoliticalMatchDatabase();

  if (!db) {
    return null;
  }

  return new Promise((resolve) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(questionnaireVersion);

    request.onsuccess = () => {
      resolve((request.result as PoliticalMatchLocalState | undefined) ?? null);
    };

    request.onerror = () => {
      resolve(null);
    };
  });
}

async function writeStateToIndexedDb(state: PoliticalMatchLocalState): Promise<void> {
  const db = await openPoliticalMatchDatabase();

  if (!db) {
    return;
  }

  await new Promise<void>((resolve) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(state);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      resolve();
    };

    transaction.oncomplete = () => {
      resolve();
    };

    transaction.onerror = () => {
      resolve();
    };
  });
}

async function deleteStateFromIndexedDb(questionnaireVersion: string): Promise<void> {
  const db = await openPoliticalMatchDatabase();

  if (!db) {
    return;
  }

  await new Promise<void>((resolve) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(questionnaireVersion);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      resolve();
    };

    transaction.oncomplete = () => {
      resolve();
    };

    transaction.onerror = () => {
      resolve();
    };
  });
}

async function clearIndexedDb(): Promise<void> {
  const db = await openPoliticalMatchDatabase();

  if (!db) {
    return;
  }

  await new Promise<void>((resolve) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      resolve();
    };

    transaction.oncomplete = () => {
      resolve();
    };

    transaction.onerror = () => {
      resolve();
    };
  });
}

export async function loadPoliticalMatchLocalState(questionnaireVersion: string): Promise<PoliticalMatchLocalState | null> {
  const cachedState = memoryStateStore.get(questionnaireVersion) ?? null;
  const persistedState = await readStateFromIndexedDb(questionnaireVersion);

  return persistedState ?? cachedState;
}

export async function savePoliticalMatchLocalState(state: PoliticalMatchLocalState): Promise<void> {
  const clonedState = cloneState(state);
  memoryStateStore.set(clonedState.questionnaireVersion, clonedState);

  await writeStateToIndexedDb(clonedState);
}

export async function clearPoliticalMatchLocalState(questionnaireVersion?: string): Promise<void> {
  if (typeof questionnaireVersion === 'string') {
    memoryStateStore.delete(questionnaireVersion);
    await deleteStateFromIndexedDb(questionnaireVersion);
    return;
  }

  memoryStateStore.clear();
  await clearIndexedDb();
}
